import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useLanguageStore } from "@/stores/languageStore";
import { logger } from "@/infrastructure/services/LoggerService";
import { AVAILABLE_LANGUAGE_TAGS } from "@/domain/types";
import { emitLocaleChange } from "@/shared/composables/useLocaleChange";
/**
 * Composable for locale management
 * Handles language changes, storage, and i18n integration using Pinia store
 */
export function useLocale() {
    const { locale } = useI18n();
    const languageStore = useLanguageStore();
    /**
     * Set the current locale
     */
    const setLocale = (newLocale, source = "manual") => {
        // Update store
        languageStore.setLocale(newLocale, source);
        // Update i18n
        locale.value = languageStore.currentLocale;
        // Update document language
        document.documentElement.lang = languageStore.currentLocale;
        // Emit locale change event
        // This triggers Keycloak sync via LocaleSyncService
        emitLocaleChange(newLocale, source);
        logger.debug(`Locale set to: ${languageStore.currentLocale} (source: ${source})`);
    };
    /**
     * Set locale from Keycloak token (highest priority)
     */
    const setLocaleFromKeycloak = (keycloakLocale) => {
        if (languageStore.currentLocale == keycloakLocale && languageStore.source === "keycloak") {
            logger.debug(`Locale from Keycloak (${keycloakLocale}) is already set as current locale.`);
            return true;
        }
        const previousLocale = languageStore.currentLocale;
        const success = languageStore.setLocaleFromKeycloak(keycloakLocale);
        if (success) {
            // Update i18n and document
            locale.value = languageStore.currentLocale;
            document.documentElement.lang = languageStore.currentLocale;
            // Emit locale change event only if locale actually changed
            if (previousLocale !== languageStore.currentLocale) {
                emitLocaleChange(languageStore.currentLocale, "keycloak");
            }
        }
        return success;
    };
    /**
     * Set locale from API (for public pages without Keycloak)
     * Has higher priority than localStorage but lower than Keycloak
     */
    const setLocaleFromApi = (apiLocale) => {
        const success = languageStore.setLocaleFromApi(apiLocale);
        if (success) {
            // Update i18n and document
            locale.value = languageStore.currentLocale;
            document.documentElement.lang = languageStore.currentLocale;
            // Emit locale change event
            emitLocaleChange(languageStore.currentLocale, "api");
        }
        return success;
    };
    // Computed property for reactive current locale
    const currentLocale = computed(() => languageStore.currentLocale);
    return {
        currentLocale,
        availableLocales: AVAILABLE_LANGUAGE_TAGS,
        setLocale,
        setLocaleFromKeycloak,
        setLocaleFromApi,
        isLanguageTagSupported: languageStore.isLanguageTagSupported,
    };
}
